// Which language the site is shown in.
//
// The order of precedence (decided with the product owner):
//   1. the visitor's own choice (cookie, mirrored in the profile for signed-in users)
//   2. the browser language: German -> German, everything else -> English
// Location (IP) is deliberately not used: it says little about the language
// and adds a third party and a privacy surface.

export const SUPPORTED_LOCALES = ['en', 'de'];
export const DEFAULT_LOCALE = 'en';
export const LOCALE_COOKIE = 'sihmo_lang';
// Language tags for <html lang>, react-intl and Moment.
export const INTL_LOCALE = { en: 'en-US', de: 'de' };

/** 'de-AT' -> 'de', 'EN_us' -> 'en', 'zh-CN' -> null */
export const normalizeLocale = tag => {
  const base = String(tag || '')
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  return SUPPORTED_LOCALES.includes(base) ? base : null;
};

/** Reads the language cookie out of a Cookie header / document.cookie string. */
export const localeFromCookie = cookieString => {
  const match = String(cookieString || '').match(
    new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]+)`)
  );
  return match ? normalizeLocale(decodeURIComponent(match[1])) : null;
};

/**
 * Only the visitor's top-ranked language counts: a Chinese browser that lists
 * German as a distant fallback should get English, not German.
 * "de-DE,de;q=0.9,en;q=0.8" -> 'de', "zh-CN,zh;q=0.9,de;q=0.5" -> 'en'.
 */
export const localeFromAcceptLanguage = header => {
  const ranked = String(header || '')
    .split(',')
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(';');
      const q = params.map(p => p.trim()).find(p => p.startsWith('q='));
      const weight = q ? parseFloat(q.slice(2)) : 1;
      return { tag: tag.trim(), weight: Number.isNaN(weight) ? 0 : weight, index };
    })
    .filter(l => l.tag && l.tag !== '*' && l.weight > 0)
    .sort((a, b) => b.weight - a.weight || a.index - b.index);
  return ranked.length > 0 ? normalizeLocale(ranked[0].tag) || DEFAULT_LOCALE : null;
};

export const resolveLocale = ({ cookie, acceptLanguage } = {}) =>
  localeFromCookie(cookie) || localeFromAcceptLanguage(acceptLanguage) || DEFAULT_LOCALE;

/** Browser only: the language the page is shown in (the server tells us what it rendered). */
export const currentLocale = () =>
  (typeof window !== 'undefined' && normalizeLocale(window.__SIHMO_LOCALE__)) ||
  (typeof document !== 'undefined'
    ? resolveLocale({ cookie: document.cookie, acceptLanguage: navigator.language })
    : DEFAULT_LOCALE);

/** Browser only: remember the choice for a year. */
export const writeLocaleCookie = locale => {
  if (typeof document === 'undefined' || !SUPPORTED_LOCALES.includes(locale)) {
    return;
  }
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
};
